import { Subscribable } from '@volkswagen-onehub/feature-hub-utils';
import { NotificationTypeV1 as NotificationType, NotificationCloseTriggerV1 as NotificationCloseTrigger, NotificationCloseTypeV1 as NotificationCloseType, } from './types';
export * from './types';
function createUniqueId(owner, id) {
    return `${owner}-${id}`;
}
function checkForValidAutoClose(...objects) {
    return objects.some((obj) => obj !== undefined);
}
/**
 * All Notifications should close automatically.
 * Exceptions are when one or more of the following optional Notifications are set to true:
 * ********************
 * Close button -
 * CTA Text button -
 * CTA Secondary button
 * ********************
 */
function getAutoCloseAfter(autoCloseAfter, type, userCloseable, notAutoClose) {
    if (userCloseable || notAutoClose)
        return 0; // autoCloseAfter is not set
    if (typeof autoCloseAfter === 'number') {
        return Math.max(Math.min(autoCloseAfter, 10), 0);
    }
    // autoCloseAfter is for all types
    return type === NotificationType.POPUP_GENERIC ||
        NotificationType.POPUP_SUCCESS ||
        NotificationType.POPUP_ERROR
        ? 6
        : 0;
}
export class NotificationStoreV1 extends Subscribable {
    constructor(logger) {
        super();
        this.logger = logger;
        this.notifications = [];
    }
    createNotification(owner, type, id, content, options = {}) {
        if (this.isOpen(owner, id)) {
            const message = `Notification with ID "${id}" is already open for consumer "${owner}"`;
            this.logger.warn(message);
            throw new Error(message);
        }
        const optionsWithDefaults = {
            title: options.title || '',
            icon: options.icon || null,
            secondaryButton: options.secondaryButton || null,
            textButton: options.textButton || null,
            darkThemeEnabled: typeof options.darkThemeEnabled === 'boolean' ? options.darkThemeEnabled : false,
            closeOn: options.closeOn || NotificationCloseType.NAVIGATION,
            autoCloseAfter: getAutoCloseAfter(options.autoCloseAfter, type, typeof options.userCloseable === 'boolean' ? options.userCloseable : true, checkForValidAutoClose(options.secondaryButton, options.textButton)),
            onClose: options.onClose || (() => undefined),
            userCloseable: typeof options.userCloseable === 'boolean' ? options.userCloseable : true,
        };
        const notification = {
            type,
            content,
            id: createUniqueId(owner, id),
            options,
            handleUserClose: optionsWithDefaults.userCloseable
                ? () => this.closeNotification(owner, id, NotificationCloseTrigger.CLICK)
                : undefined,
        };
        const autoCloseTimer = optionsWithDefaults.autoCloseAfter
            ? setTimeout(() => this.closeNotification(owner, id, NotificationCloseTrigger.AUTO), optionsWithDefaults.autoCloseAfter * 1000)
            : undefined;
        this.notifications.unshift({
            autoCloseTimer,
            notification,
            closeOn: optionsWithDefaults.closeOn,
            onClose: optionsWithDefaults.onClose,
        });
        this.updateInternal();
    }
    closeNotification(owner, id, triggerEvents) {
        const fullId = createUniqueId(owner, id);
        const index = this.notifications.findIndex((notification) => notification.notification.id === fullId);
        if (index === -1) {
            return;
        }
        const notification = this.notifications[index];
        this.closeKnownNotification(index);
        this.fireEventsAfterClose([notification], triggerEvents);
    }
    isOpen(owner, id) {
        const fullId = createUniqueId(owner, id);
        return Boolean(this.notifications.find((notification) => notification.notification.id === fullId));
    }
    getNotifications(types) {
        if (!types) {
            return this.notifications.map((n) => (Object.assign({}, n.notification)));
        }
        if (!types.length) {
            return [];
        }
        return this.notifications
            .map((notification) => types.indexOf(notification.notification.type) !== -1
            ? Object.assign({}, notification.notification) : undefined)
            .filter((n) => Boolean(n));
    }
    announceIntegratorNavigation() {
        const closedNotifications = [];
        // Start closing the notifications from the back to make sure that we don't run into any
        // problems with moved up items
        for (let i = this.notifications.length - 1; i >= 0; i -= 1) {
            const notification = this.notifications[i];
            if (notification.closeOn === NotificationCloseType.NAVIGATION) {
                closedNotifications.push(notification);
                this.closeKnownNotification(i);
            }
        }
        this.fireEventsAfterClose(closedNotifications, NotificationCloseTrigger.NAVIGATION);
    }
    closeKnownNotification(index) {
        const notification = this.notifications[index];
        if (notification.autoCloseTimer) {
            clearTimeout(notification.autoCloseTimer);
        }
        this.notifications.splice(index, 1);
    }
    fireEventsAfterClose(notifications, trigger) {
        this.updateInternal();
        notifications.forEach((notification) => {
            if (notification.onClose) {
                notification.onClose(trigger);
            }
        });
    }
}
//# sourceMappingURL=store.js.map